import React, { useCallback, useEffect, useState } from 'react';
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo, FiX } from 'react-icons/fi';
import { useFomaularioStorage } from '../../storage';
import styles from './Notify.module.css';

const AUTO_DISMISS_MS = 3500;

// API pública.
//   notify('texto')                 -> info (default)
//   notify('texto', 'success')      -> tipo explícito
//   notify({ title, type })         -> forma objeto
//
// O `id` incremental força remount do toast (via key) pra reanimar quando
// o usuário dispara várias notifs em sequência rápida.
export function notify(titleOrConfig, type = 'info') {
    const config = typeof titleOrConfig === 'string'
        ? { title: titleOrConfig, type }
        : { title: titleOrConfig?.title || '', type: titleOrConfig?.type || 'info' };

    if (!config.title) return;

    const current = useFomaularioStorage.getState().dados.openNotify;
    const setOpenNotify = useFomaularioStorage.getState().dispatch.setOpenNotify;
    setOpenNotify({
        open: true,
        title: config.title,
        type: config.type,
        id: (current?.id || 0) + 1,
    });
}

const TYPE_ICONS = {
    success: FiCheckCircle,
    error: FiAlertCircle,
    warning: FiAlertTriangle,
    info: FiInfo,
};

export default function NotifyComp() {
    const openNotify = useFomaularioStorage(state => state.dados.openNotify);
    const setOpenNotify = useFomaularioStorage.getState().dispatch.setOpenNotify;
    const [exiting, setExiting] = useState(false);

    const handleClose = useCallback(() => {
        setExiting(true);
        // Aguarda a animação de saída antes de zerar o estado.
        setTimeout(() => {
            setOpenNotify({ open: false, title: '', type: 'info', id: openNotify?.id || 0 });
            setExiting(false);
        }, 200);
    }, [setOpenNotify, openNotify?.id]);

    // Auto-dismiss — reinicia a cada nova notif (id muda).
    useEffect(() => {
        if (!openNotify?.open) return;
        setExiting(false);
        const t = setTimeout(handleClose, AUTO_DISMISS_MS);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openNotify?.id, openNotify?.open]);

    if (!openNotify?.open) return null;

    const type = openNotify.type || 'info';
    const Icon = TYPE_ICONS[type] || FiInfo;
    const capitalized = type.charAt(0).toUpperCase() + type.slice(1);

    return (
        <div className={styles.container} role="region" aria-label="Notificações">
            <div
                key={openNotify.id}
                className={`${styles.toast} ${styles[type]} ${exiting ? styles.toastExit : ''}`}
                role={type === 'error' ? 'alert' : 'status'}
            >
                <span className={`${styles.iconWrap} ${styles['iconWrap' + capitalized]}`}>
                    <Icon size={16} strokeWidth={2.4} />
                </span>
                <div className={styles.body}>
                    <p className={styles.title}>{openNotify.title}</p>
                </div>
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={handleClose}
                    aria-label="Fechar notificação"
                >
                    <FiX size={16} />
                </button>
                <div
                    className={`${styles.progress} ${styles['progress' + capitalized]}`}
                    style={{ '--duration': `${AUTO_DISMISS_MS}ms` }}
                />
            </div>
        </div>
    );
}
